import { useEffect, useMemo, useState } from 'react';

import { Field, Empty, Search } from '../../components/ui/bits.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { Segments } from '../../components/AppHeader.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import * as api from '../../lib/api.js';
import { fmtDate, today, addMonths } from '../../lib/format.js';
import { parseProgramText, programToText, sklopiIzBesedila, slug } from '../../lib/program.js';
import { PLANI } from '../../config.js';
import { useDebounced } from '../../hooks.js';

/*
  Platforma — samo Luka.

    Šole      vse šole, status in paket, odpri kot inštruktor
    Prijave   prijave s strani „Za šole“: pregled programa, odobritev
    Nova šola ročni vnos (npr. po telefonu)

  Odobritev ustvari šolo, lastnika (po e-pošti iz prijave) in program v
  eni transakciji v bazi (odobri_prijavo). Nič ni treba popravljati ročno.
*/

const STATUS = {
  preizkus: ['Preizkus', 'wait'],
  aktivna: ['Aktivna', 'ok'],
  zamrznjena: ['Zamrznjena', 'bad'],
  zaprta: ['Zaprta', 'mute'],
};

export function Platforma({ onOpenSola, currentSolaId }) {
  const { toast } = useUI();
  const [seg, setSeg] = useState('sole');
  const [data, setData] = useState(null);

  const reload = () => api.loadPlatforma().then(setData).catch((e) => toast.bad(e.message));
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nove = data?.prijave.filter((p) => p.status === 'nova').length ?? 0;

  return (
    <div className="stack">
      {data && (
        <section className="tiles" aria-label="Platforma">
          <Tile label="Aktivnih šol" value={data.sole.filter((s) => s.status === 'aktivna').length} />
          <Tile label="Na preizkusu" value={data.sole.filter((s) => s.status === 'preizkus').length} tone="wait" />
          <Tile label="Novih prijav" value={nove} tone={nove ? 'brand' : undefined} />
          <Tile label="Tečajnikov skupaj" value={data.sole.reduce((a, s) => a + s.aktivnih, 0)} />
        </section>
      )}

      <Segments
        label="Platforma"
        value={seg}
        onChange={setSeg}
        items={[
          { id: 'sole', icon: 'school', label: 'Šole' },
          { id: 'prijave', icon: 'mail', label: 'Prijave', badge: nove },
          { id: 'nova', icon: 'plus', label: 'Nova šola' },
        ]}
      />

      {!data ? (
        <p className="t-sm t-dim" role="status">Nalagam …</p>
      ) : seg === 'sole' ? (
        <Sole sole={data.sole} onChanged={reload} onOpen={onOpenSola} currentSolaId={currentSolaId} />
      ) : seg === 'prijave' ? (
        <Prijave prijave={data.prijave} onChanged={reload} />
      ) : (
        <NovaSola onCreated={reload} />
      )}
    </div>
  );
}

function Tile({ label, value, tone }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value" style={{ color: tone ? `var(--${tone}-${tone === 'brand' ? 'ink' : 'text'})` : undefined }}>
        {value}
      </span>
    </div>
  );
}

/* ── Šole ───────────────────────────────────────────────────── */

function Sole({ sole, onChanged, onOpen, currentSolaId }) {
  const [query, setQuery] = useState('');
  const [edit, setEdit] = useState(null);
  const q = useDebounced(query, 150);
  const list = useMemo(
    () => sole.filter((s) => `${s.ime} ${s.slug} ${s.drzava} ${s.email ?? ''}`.toLowerCase().includes(q.toLowerCase())),
    [sole, q],
  );

  return (
    <div className="stack">
      <Search value={query} onChange={setQuery} placeholder="Išči šolo" />
      <div className="card list">
        {list.map((s) => {
          const [label, tone] = STATUS[s.status] ?? [s.status, 'mute'];
          const plan = PLANI.find((p) => p.id === s.plan);
          const dni = s.velja_do ? Math.round((new Date(s.velja_do) - new Date()) / 86400000) : null;
          return (
            <div key={s.id} className="list__row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--s-1)' }}>
              <div className="row row--wrap">
                {s.logo_url ? (
                  <img src={s.logo_url} alt="" style={{ height: 28, width: 'auto', maxWidth: 60, objectFit: 'contain' }} />
                ) : (
                  <Icon name="school" size={22} className="t-dimmer" />
                )}
                <span className="t-md t-bold" style={{ flex: '1 1 160px', minWidth: 0 }}>
                  {s.ime}
                  <span className="t-xs t-dimmer num" style={{ display: 'block', fontWeight: 500 }}>{s.slug} · {s.drzava}</span>
                </span>
                <span className={`badge badge--${tone}`}>{label}</span>
                {s.id === currentSolaId ? (
                  <span className="badge badge--brand">odprta</span>
                ) : (
                  <button className="btn btn--sm btn--secondary" onClick={() => onOpen(s.id)}>
                    Odpri
                  </button>
                )}
                <button className="btn btn--sm btn--ghost" onClick={() => setEdit(s)}>
                  <Icon name="edit" size={14} /> Uredi
                </button>
              </div>
              <div className="t-xs t-dim num row row--wrap" style={{ gap: 'var(--s-3)' }}>
                <span>{plan?.ime ?? s.plan}</span>
                <span>{s.aktivnih}{s.max_tecajnikov ? `/${s.max_tecajnikov}` : ''} tečajnikov</span>
                <span>
                  {s.velja_do ? `velja do ${fmtDate(s.velja_do)}` : 'brez omejitve'}
                  {dni != null && dni < 0 && <span className="t-bad"> · potekla</span>}
                  {dni != null && dni >= 0 && dni <= 30 && <span className="t-wait"> · čez {dni} dni</span>}
                </span>
                {s.email && <span>{s.email}</span>}
                <span>koda {s.koda ?? '—'}</span>
              </div>
            </div>
          );
        })}
        {list.length === 0 && <Empty icon="school" title="Ni šol" />}
      </div>

      {edit && <UrediSolo sola={edit} onClose={() => setEdit(null)} onSaved={onChanged} />}
    </div>
  );
}

function UrediSolo({ sola, onClose, onSaved }) {
  const { toast, confirm } = useUI();
  const [f, setF] = useState({
    status: sola.status,
    plan: sola.plan,
    velja_do: sola.velja_do ?? '',
    max_tecajnikov: sola.max_tecajnikov ?? '',
    koda: sola.koda ?? '',
    opombe: sola.opombe ?? '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateSola(sola.id, {
        status: f.status,
        plan: f.plan,
        velja_do: f.velja_do || null,
        max_tecajnikov: f.max_tecajnikov === '' ? null : Number(f.max_tecajnikov),
        koda: f.koda.trim().toUpperCase() || null,
        opombe: f.opombe.trim() || null,
      });
      toast.ok('Šola je posodobljena.');
      await onSaved();
      onClose();
    } catch (e) {
      toast.bad(e.message ?? 'Ni uspelo.');
    } finally {
      setBusy(false);
    }
  };

  const izbrisi = async () => {
    const ok = await confirm({
      title: `Izbrišem šolo ${sola.ime}?`,
      body: 'Šola nima tečajnikov. Izbrisani bodo inštruktorji, program in nastavitve.',
      confirmLabel: 'Izbriši šolo',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteSola(sola.id);
      toast.ok('Šola je izbrisana.');
      await onSaved();
      onClose();
    } catch (e) {
      toast.bad(e.message ?? 'Ni uspelo.');
    }
  };

  return (
    <Modal
      eyebrow="Platforma"
      title={sola.ime}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save} disabled={busy}>
            {busy ? 'Shranjujem …' : 'Shrani'}
          </button>
          {sola.aktivnih === 0 && sola.plan !== 'interno' && (
            <button className="btn btn--danger" onClick={izbrisi} disabled={busy}>
              Izbriši
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose}>Prekliči</button>
        </>
      }
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="Status" id="e-status">
            <select id="e-status" className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {Object.entries(STATUS).map(([k, [l]]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </Field>
          <Field label="Paket" id="e-plan">
            <select id="e-plan" className="input" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
              {PLANI.map((p) => (
                <option key={p.id} value={p.id}>{p.ime}{p.cena ? ` (${p.cena} €)` : ''}</option>
              ))}
            </select>
          </Field>
          <Field label="Velja do" id="e-velja" hint="Prazno = brez omejitve.">
            <input id="e-velja" type="date" className="input" value={f.velja_do} onChange={(e) => setF({ ...f, velja_do: e.target.value })} />
          </Field>
          <Field label="Največ aktivnih tečajnikov" id="e-max" hint="Prazno = neomejeno.">
            <input id="e-max" type="number" min="1" className="input" value={f.max_tecajnikov} onChange={(e) => setF({ ...f, max_tecajnikov: e.target.value })} />
          </Field>
          <Field label="Koda za vpis" id="e-koda">
            <input id="e-koda" className="input" value={f.koda} onChange={(e) => setF({ ...f, koda: e.target.value })} />
          </Field>
        </div>
        <Field label="Opombe platforme (šola jih ne vidi)" id="e-op">
          <textarea id="e-op" className="input" rows={3} value={f.opombe} onChange={(e) => setF({ ...f, opombe: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

/* ── Prijave ────────────────────────────────────────────────── */

function Prijave({ prijave, onChanged }) {
  const [odpri, setOdpri] = useState(null);
  const [filter, setFilter] = useState('nova');
  const list = prijave.filter((p) => (filter === 'vse' ? true : p.status === filter));

  return (
    <div className="stack">
      <div className="tabs" role="tablist" aria-label="Filter prijav">
        {[
          ['nova', 'Nove'],
          ['odobrena', 'Odobrene'],
          ['zavrnjena', 'Zavrnjene'],
          ['vse', 'Vse'],
        ].map(([v, l]) => (
          <button key={v} className="tab" role="tab" aria-selected={filter === v} onClick={() => setFilter(v)}>
            {l} ({prijave.filter((p) => (v === 'vse' ? true : p.status === v)).length})
          </button>
        ))}
      </div>

      <div className="card list">
        {list.map((p) => (
          <button key={p.id} className="list__row" onClick={() => setOdpri(p)} style={{ textAlign: 'left' }}>
            <Icon name="school" size={22} className="t-brand" />
            <span className="spacer">
              <span className="t-md t-bold" style={{ display: 'block' }}>
                {p.ime_sole} <span className="t-xs t-dimmer">{p.drzava} · {p.jezik}</span>
              </span>
              <span className="t-xs t-dim">
                {p.kontakt_ime} · {p.email} · {fmtDate(p.created_at.slice(0, 10))}
                {p.st_tecajnikov ? ` · ~${p.st_tecajnikov} tečajnikov` : ''}
              </span>
            </span>
            <span className={`badge ${p.status === 'nova' ? 'badge--brand' : p.status === 'odobrena' ? 'badge--ok' : 'badge--mute'}`}>
              {p.status}
            </span>
            <Icon name="right" size={16} className="t-dimmer" />
          </button>
        ))}
        {list.length === 0 && <Empty icon="mail" title="Ni prijav" hint="Prijave pridejo z javne strani Za šole." />}
      </div>

      {odpri && <PrijavaModal prijava={odpri} onClose={() => setOdpri(null)} onChanged={onChanged} />}
    </div>
  );
}

function PrijavaModal({ prijava: p, onClose, onChanged }) {
  const { toast, confirm } = useUI();
  const [slugV, setSlugV] = useState(slug(p.ime_sole));
  const [plan, setPlan] = useState('preizkus');
  const [veljaDo, setVeljaDo] = useState(addMonths(today(), 1));
  const [max, setMax] = useState('10');
  const [programText, setProgramText] = useState(p.program ?? '');
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => (programText.trim() ? parseProgramText(programText) : []), [programText]);
  const labels = useMemo(() => sklopiIzBesedila(programText), [programText]);

  const odobri = async () => {
    if (!/^[a-z0-9-]{2,40}$/.test(slugV)) {
      toast.bad('Oznaka šole: male črke, številke in vezaj, 2–40 znakov.');
      return;
    }
    const ok = await confirm({
      title: 'Odobrim prijavo?',
      body: `Ustvari šolo „${p.ime_sole}“ (${slugV}), lastnika ${p.email} in ${rows.length ? `program s ${rows.length} vajami` : 'Perutov program kot izhodišče'}.`,
      confirmLabel: 'Odobri',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const solaId = await api.odobriPrijavo({
        prijavaId: p.id,
        slug: slugV,
        plan,
        veljaDo: veljaDo || null,
        max: max === '' ? null : Number(max),
        program: rows.length ? rows : null,
      });
      if (rows.length && Object.keys(labels).length) {
        const s = await api.loadSola(solaId);
        await api.updateSola(solaId, { nastavitve: { ...(s?.nastavitve ?? {}), sklopi: labels, program_lasten: true } });
      }
      toast.ok('Šola je ustvarjena. Lastnik se prijavi z Googlom z e-pošto iz prijave.', { duration: 8000 });
      await onChanged();
      onClose();
    } catch (e) {
      toast.bad(e.message ?? 'Odobritev ni uspela.');
    } finally {
      setBusy(false);
    }
  };

  const zavrni = async () => {
    const ok = await confirm({ title: 'Zavrnem prijavo?', body: p.ime_sole, confirmLabel: 'Zavrni', danger: true });
    if (!ok) return;
    try {
      await api.zavrniPrijavo(p.id, null);
      toast.ok('Prijava je zavrnjena.');
      await onChanged();
      onClose();
    } catch (e) {
      toast.bad(e.message ?? 'Ni uspelo.');
    }
  };

  return (
    <Modal
      eyebrow={`Prijava · ${fmtDate(p.created_at.slice(0, 10))}`}
      title={p.ime_sole}
      onClose={onClose}
      footer={
        p.status === 'nova' ? (
          <>
            <button className="btn btn--primary" onClick={odobri} disabled={busy}>
              {busy ? 'Ustvarjam …' : 'Odobri in ustvari šolo'}
            </button>
            <button className="btn btn--danger" onClick={zavrni} disabled={busy}>Zavrni</button>
            <button className="btn btn--ghost" onClick={onClose}>Zapri</button>
          </>
        ) : (
          <button className="btn btn--primary" onClick={onClose}>Zapri</button>
        )
      }
    >
      <div className="stack">
        <dl className="kv">
          <dt>Kontakt</dt>
          <dd>{p.kontakt_ime} · <a href={`mailto:${p.email}`}>{p.email}</a>{p.telefon ? ` · ${p.telefon}` : ''}</dd>
          <dt>Država / jezik</dt>
          <dd>{p.drzava} / {p.jezik}</dd>
          {p.splet && (
            <>
              <dt>Splet</dt>
              <dd><a href={p.splet.startsWith('http') ? p.splet : `https://${p.splet}`} target="_blank" rel="noopener noreferrer">{p.splet}</a></dd>
            </>
          )}
          {p.st_tecajnikov && (
            <>
              <dt>Tečajnikov na leto</dt>
              <dd>{p.st_tecajnikov}</dd>
            </>
          )}
          {p.vir && (
            <>
              <dt>Vir</dt>
              <dd>{p.vir}</dd>
            </>
          )}
          {p.sporocilo && (
            <>
              <dt>Sporočilo</dt>
              <dd style={{ whiteSpace: 'pre-line' }}>{p.sporocilo}</dd>
            </>
          )}
          {p.status !== 'nova' && (
            <>
              <dt>Status</dt>
              <dd>{p.status} · {p.obravnavano ? fmtDate(p.obravnavano.slice(0, 10)) : ''}</dd>
            </>
          )}
        </dl>

        {p.status === 'nova' && (
          <>
            <hr className="hr" />
            <div className="grid-2">
              <Field label="Oznaka šole (slug)" id="o-slug" hint="V povezavi za vpis: /?sola=…">
                <input id="o-slug" className="input" value={slugV} onChange={(e) => setSlugV(e.target.value)} />
              </Field>
              <Field label="Paket" id="o-plan">
                <select id="o-plan" className="input" value={plan} onChange={(e) => setPlan(e.target.value)}>
                  {PLANI.filter((x) => x.id !== 'interno').map((x) => (
                    <option key={x.id} value={x.id}>{x.ime}{x.cena ? ` (${x.cena} €)` : ''}</option>
                  ))}
                </select>
              </Field>
              <Field label="Velja do" id="o-velja">
                <input id="o-velja" type="date" className="input" value={veljaDo} onChange={(e) => setVeljaDo(e.target.value)} />
              </Field>
              <Field label="Največ tečajnikov" id="o-max" hint="Prazno = neomejeno.">
                <input id="o-max" type="number" min="1" className="input" value={max} onChange={(e) => setMax(e.target.value)} />
              </Field>
            </div>

            <Field
              label="Program šolanja"
              id="o-program"
              hint="Vrstica = vaja, vrstica z vezajem = pod-vaja, vrstica z # = sklop. Prazno = Perutov program kot izhodišče."
            >
              <textarea id="o-program" className="input" rows={10} value={programText} onChange={(e) => setProgramText(e.target.value)} style={{ fontFamily: 'var(--font-num)', fontSize: 'var(--fs-sm)' }} />
            </Field>
            {rows.length > 0 && (
              <details className="card card--pad">
                <summary className="t-sm t-bold" style={{ cursor: 'pointer' }}>
                  Predogled: {rows.length} vaj, {rows.reduce((a, r) => a + r.subs.length, 0)} pod-vaj
                </summary>
                <pre className="t-xs t-dim" style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--s-2)' }}>{programToText(rows, labels)}</pre>
              </details>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/* ── Nova šola ročno ────────────────────────────────────────── */

function NovaSola({ onCreated }) {
  const { toast } = useUI();
  const [f, setF] = useState({ ime: '', slug: '', drzava: 'SI', jezik: 'sl', email: '', plan: 'preizkus', velja_do: addMonths(today(), 1), max: '10' });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const s = f.slug || slug(f.ime);
    if (!/^[a-z0-9-]{2,40}$/.test(s)) {
      toast.bad('Oznaka šole: male črke, številke in vezaj.');
      return;
    }
    setBusy(true);
    try {
      const sola = await api.ustvariSoloPlatforma({
        ime: f.ime.trim(),
        slug: s,
        kratko: 'Učilnica',
        drzava: f.drzava.toUpperCase(),
        jezik: f.jezik,
        email: f.email.trim() || null,
        status: f.plan === 'preizkus' ? 'preizkus' : 'aktivna',
        plan: f.plan,
        velja_do: f.velja_do || null,
        max_tecajnikov: f.max === '' ? null : Number(f.max),
        koda: `${s.replace(/[^a-z0-9]/g, '').slice(0, 4).toUpperCase()}${String(Math.floor(Math.random() * 100)).padStart(2, '0')}`,
      });
      if (f.email.trim()) await api.addClan(sola.id, f.email, 'lastnik', null);
      toast.ok(`Šola ${sola.ime} je ustvarjena (koda ${sola.koda}).`, { duration: 8000 });
      setF({ ime: '', slug: '', drzava: 'SI', jezik: 'sl', email: '', plan: 'preizkus', velja_do: addMonths(today(), 1), max: '10' });
      await onCreated();
    } catch (err) {
      toast.bad(err.message ?? 'Ni uspelo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card card--pad stack" onSubmit={save}>
      <h2 className="t-lg">Nova šola</h2>
      <p className="t-sm t-dim">Program dobi Perutovega kot izhodišče; šola ga uredi sama pod Šola → Program.</p>
      <div className="grid-2">
        <Field label="Ime šole" id="ns-ime" required>
          <input id="ns-ime" className="input" value={f.ime} onChange={(e) => setF({ ...f, ime: e.target.value, slug: f.slug || '' })} />
        </Field>
        <Field label="Oznaka (slug)" id="ns-slug" hint={`Privzeto: ${slug(f.ime) || '—'}`}>
          <input id="ns-slug" className="input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
        </Field>
        <Field label="Država (2 črki)" id="ns-drz">
          <input id="ns-drz" className="input" maxLength={2} value={f.drzava} onChange={(e) => setF({ ...f, drzava: e.target.value })} />
        </Field>
        <Field label="Jezik" id="ns-jez">
          <select id="ns-jez" className="input" value={f.jezik} onChange={(e) => setF({ ...f, jezik: e.target.value })}>
            <option value="sl">sl</option><option value="en">en</option><option value="hr">hr</option><option value="de">de</option><option value="it">it</option>
          </select>
        </Field>
        <Field label="E-pošta lastnika (Google)" id="ns-email" hint="Postane lastnik šole ob prvi prijavi.">
          <input id="ns-email" type="email" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Paket" id="ns-plan">
          <select id="ns-plan" className="input" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            {PLANI.filter((x) => x.id !== 'interno').map((x) => (
              <option key={x.id} value={x.id}>{x.ime}</option>
            ))}
          </select>
        </Field>
        <Field label="Velja do" id="ns-velja">
          <input id="ns-velja" type="date" className="input" value={f.velja_do} onChange={(e) => setF({ ...f, velja_do: e.target.value })} />
        </Field>
        <Field label="Največ tečajnikov" id="ns-max">
          <input id="ns-max" type="number" min="1" className="input" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} />
        </Field>
      </div>
      <button className="btn btn--primary" type="submit" disabled={busy || !f.ime.trim()} style={{ alignSelf: 'flex-start' }}>
        {busy ? 'Ustvarjam …' : 'Ustvari šolo'}
      </button>
    </form>
  );
}

// Platforma ob urejanju programa uporabi iste pomočnike kot šola.
export { parseProgramText };
